/**
 * What the progress screen's charts draw, computed here so the `.tsx` only
 * renders it — the testable seam, for the reason `progress.ts` gives.
 *
 * Weeks are bucketed in the browser, not on the server: a week starts on the
 * learner's own local Monday, and only the browser knows their timezone.
 *
 * `KidCharts` has no field for a strip of outcomes, the same way `KidProgress`
 * has none for a struggle (progress design §3): the chart a child cannot be
 * shown is one the type cannot hold.
 */
import { patternLabel, visibleIn, type WordRegister } from './practice.ts';
import type { PracticeItemRow, ProgressSession } from './progress.ts';
import { startOfDay } from './sessions.ts';

/** Twelve weeks: a trend for someone who talks a few times a week, and a cheap query. */
export const WEEKS = 12;

/** How many patterns get a strip — the busiest, as "Working on" ranks them. */
export const MAX_STRIPS = 6;

export type Week = { start: number };

/** The Monday, 00:00 local, of the week containing `d`. */
function mondayOf(d: Date): Date {
  const day = new Date(startOfDay(d));
  // `setDate`, not millisecond arithmetic: a week across a DST change is not
  // 7 × 24 hours, and `progress.dst.test.ts` records what that costs.
  day.setDate(day.getDate() - ((day.getDay() + 6) % 7));
  return day;
}

/** The window's weeks, oldest first, the last one containing `now`. */
export function recentWeeks(now: Date, n: number = WEEKS): Week[] {
  const first = mondayOf(now);
  first.setDate(first.getDate() - 7 * (n - 1));
  return Array.from({ length: n }, (_, i) => {
    const d = new Date(first);
    d.setDate(d.getDate() + 7 * i);
    return { start: d.getTime() };
  });
}

/** Where the window begins — what the query's `since` asks for. */
export function windowStart(now: Date, n: number = WEEKS): Date {
  return new Date(recentWeeks(now, n)[0].start);
}

/** Which week a timestamp falls in, or -1 outside the window. */
function weekOf(weeks: readonly Week[], iso: string): number {
  const t = Date.parse(iso);
  for (let i = weeks.length - 1; i >= 0; i--) if (t >= weeks[i].start) return i;
  return -1;
}

export type WeekCount = { start: number; conversations: number };

export function conversationsPerWeek(sessions: readonly { startedAt: string }[], weeks: readonly Week[]): WeekCount[] {
  const counts = weeks.map((w) => ({ start: w.start, conversations: 0 }));
  for (const s of sessions) {
    const i = weekOf(weeks, s.startedAt);
    if (i >= 0) counts[i].conversations++;
  }
  return counts;
}

export type ChartWord = { firstMetAt: string; source: string };

export type LearnedPoint = { start: number; words: number; patterns: number };

/**
 * New words met, and patterns first got right, since the window began —
 * running totals from zero rather than lifetime counts, which would need a
 * count from before the window for a number nobody asked for.
 *
 * A pattern counts once, in the week it was first used correctly. Only
 * `USED_CORRECTLY` is a win: a recurrence is not something learned. Words pass
 * the vocabulary register gate first, so a child is never counted a word that
 * was a correction.
 */
export function learnedOverTime(
  words: readonly ChartWord[],
  sessions: readonly ProgressSession[],
  weeks: readonly Week[],
  register: WordRegister,
): LearnedPoint[] {
  const newWords = weeks.map(() => 0);
  for (const w of words) {
    if (!visibleIn(w.source, register)) continue;
    const i = weekOf(weeks, w.firstMetAt);
    if (i >= 0) newWords[i]++;
  }

  const firstWin = new Map<string, number>();
  for (const s of sessions) {
    const i = weekOf(weeks, s.startedAt);
    if (i < 0) continue;
    for (const p of s.practice) {
      if (p.outcome !== 'USED_CORRECTLY') continue;
      const seen = firstWin.get(p.item.id);
      if (seen === undefined || i < seen) firstWin.set(p.item.id, i);
    }
  }
  const newPatterns = weeks.map(() => 0);
  for (const i of firstWin.values()) newPatterns[i]++;

  let totalWords = 0;
  let patterns = 0;
  return weeks.map((w, i) => {
    totalWords += newWords[i];
    patterns += newPatterns[i];
    return { start: w.start, words: totalWords, patterns };
  });
}

/** One week of one pattern: got it, came back, targeted but did not come up, or not practised. */
export type StripMark = 'got' | 'back' | 'missed' | null;

export type PatternStrip = { itemId: string; label: string; marks: StripMark[] };

const MARK: Record<string, StripMark> = {
  USED_CORRECTLY: 'got',
  RECURRED: 'back',
  TARGETED: 'missed',
};

/**
 * Per pattern, the latest outcome in each week — progress design §3's status
 * line ("the most recent outcome"), drawn over time.
 *
 * Only patterns practised in the window get a strip, busiest first as
 * `items` already arrives, and at most `MAX_STRIPS` of them: a strip with no
 * marks says nothing, and a dozen rows stop being read.
 */
export function patternStrips(
  items: readonly PracticeItemRow[],
  sessions: readonly ProgressSession[],
  weeks: readonly Week[],
): PatternStrip[] {
  const byTime = [...sessions].sort((a, b) => Date.parse(a.startedAt) - Date.parse(b.startedAt));
  const marks = new Map<string, StripMark[]>();
  for (const s of byTime) {
    const i = weekOf(weeks, s.startedAt);
    if (i < 0) continue;
    for (const p of s.practice) {
      const mark = MARK[p.outcome];
      if (mark === undefined) continue;
      const row = marks.get(p.item.id) ?? weeks.map((): StripMark => null);
      row[i] = mark;
      marks.set(p.item.id, row);
    }
  }
  return items
    .filter((item) => marks.has(item.id))
    .slice(0, MAX_STRIPS)
    .map((item) => ({ itemId: item.id, label: patternLabel(item.pattern), marks: marks.get(item.id)! }));
}

/** A session's summed learner speech, as `Session.fluency` carries it. */
export type SessionSpeech = { words: number; speakingMs: number; speechRateWpm?: number | null };

export type ChartSession = ProgressSession & { fluency?: SessionSpeech | null };

export type TalkWeek = { start: number; minutes: number };

export type PaceWeek = { start: number; wpm: number | null };

/**
 * Minutes talked and speaking pace per week, from each session's summed
 * fluency (progress charts sub-project B).
 *
 * Pace is weighted by words, as the server weights it within a session, so a
 * short session does not count as much as a long one. A week with no measured
 * words has no pace — `null`, a gap in the line — because a zero would draw a
 * learner who did not speak as one who spoke at no speed.
 */
export function speechPerWeek(
  sessions: readonly ChartSession[],
  weeks: readonly Week[],
): { talked: TalkWeek[]; pace: PaceWeek[] } {
  const ms = weeks.map(() => 0);
  const words = weeks.map(() => 0);
  const weighted = weeks.map(() => 0);
  for (const s of sessions) {
    const f = s.fluency;
    if (!f) continue;
    const i = weekOf(weeks, s.startedAt);
    if (i < 0) continue;
    ms[i] += f.speakingMs;
    if (f.speechRateWpm != null && f.words > 0) {
      words[i] += f.words;
      weighted[i] += f.speechRateWpm * f.words;
    }
  }
  return {
    talked: weeks.map((w, i) => ({ start: w.start, minutes: Math.round(ms[i] / 6_000) / 10 })),
    pace: weeks.map((w, i) => ({ start: w.start, wpm: words[i] > 0 ? Math.round(weighted[i] / words[i]) : null })),
  };
}

export type ChartInput = {
  sessions: readonly ChartSession[];
  words: readonly ChartWord[];
  items: readonly PracticeItemRow[];
  now: Date;
};

/**
 * Volume only for a child: how much they talked, never how fast. A pace
 * number invites measuring a child against a target, and `KidCharts` has no
 * field to carry one.
 */
export type KidCharts = { perWeek: WeekCount[]; learned: LearnedPoint[]; talked: TalkWeek[] };

export type AdultCharts = KidCharts & { strips: PatternStrip[]; pace: PaceWeek[] };

export function kidCharts({ sessions, words, now }: ChartInput): KidCharts {
  const weeks = recentWeeks(now);
  return {
    perWeek: conversationsPerWeek(sessions, weeks),
    learned: learnedOverTime(words, sessions, weeks, 'kid'),
    talked: speechPerWeek(sessions, weeks).talked,
  };
}

export function adultCharts({ sessions, words, items, now }: ChartInput): AdultCharts {
  const weeks = recentWeeks(now);
  const { talked, pace } = speechPerWeek(sessions, weeks);
  return {
    perWeek: conversationsPerWeek(sessions, weeks),
    learned: learnedOverTime(words, sessions, weeks, 'adult'),
    talked,
    strips: patternStrips(items, sessions, weeks),
    pace,
  };
}
